import CustomDropdown from "../common/CustomDropdown";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import MobileDropdown from "../search/MobileDropdown";
import MobileCountryDropdown from "../search/MobileCountryDropdown";
import TransferNewsDropdown from "./TransferNewsDropdown";
import TransferNewsCountryDropdown from "./TransferNewsCountryDropdown";
import TransferNewsCompetitionDropdown from "./TransferNewsCompetitionDropdown";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const TransferNewsFilters = ({
  filters,
  handleLevelChange,
  handlePositionChange,
  handleTypeChange,
  handleDateFromChange,
  handleDateToChange,
  countrySearch,
  setCountrySearch,
  showCountryDropdown,
  setShowCountryDropdown,
  divisionCountrySearch,
  setDivisionCountrySearch,
  showDivisionCountryDropdown,
  setShowDivisionCountryDropdown,
  competitionSearch,
  setCompetitionSearch,
  showCompetitionDropdown,
  setShowCompetitionDropdown,
  filteredCountries,
  filteredDivisionCountries,
  filteredCompetitions,
  positions,
  handleCountrySelect,
  handleDivisionCountrySelect,
  handleCompetitionSelect,
  getSelectedCountryName,
  getSelectedDivisionCountryName,
  getSelectedCompetitionName,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="flex flex-row flex-wrap gap-x-3 gap-y-3 py-4 px-4 sm:px-6 lg:px-12 border-b mb-6 border-[#E5E6E8] items-center w-full">
      {/* Level Filter */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.level}
            onChange={handleLevelChange}
            options={[
              { value: "pro", label: "Professional" },
              { value: "youth", label: "Youth" },
            ]}
            placeholder="Level"
            displayProperty="label"
            valueProperty="value"
          />
        ) : (
          <TransferNewsDropdown
            options={[
              { value: "pro", label: "Professional" },
              { value: "youth", label: "Youth" },
            ]}
            value={filters.level}
            onChange={handleLevelChange}
            placeholder="Level"
            displayProperty="label"
            valueProperty="value"
          />
        )}
      </div>

      {/* Player Nationality Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileCountryDropdown
            value={filters.country || ""}
            onChange={(value) => {
              const country = filteredCountries.find(c => c.alpha3code === value || c.id === value);
              if (country) {
                handleCountrySelect(country);
              }
            }}
            options={filteredCountries}
            placeholder="Player Nationality"
            searchValue={countrySearch}
            onSearchChange={setCountrySearch}
            isOpen={showCountryDropdown}
            setIsOpen={setShowCountryDropdown}
          />
        ) : (
          <TransferNewsCountryDropdown
            value={filters.country || ""}
            onChange={() => {}}
            options={filteredCountries}
            placeholder="Player Nationality"
            searchValue={countrySearch || getSelectedCountryName()}
            onSearchChange={(value) => {
              setCountrySearch(value);
              if (!value) {
                handleCountrySelect({ name: "", alpha3code: "" });
              }
            }}
            isOpen={showCountryDropdown}
            setIsOpen={setShowCountryDropdown}
            onSelect={handleCountrySelect}
          />
        )}
      </div>

      {/* Division Country Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileCountryDropdown
            value={filters.divisionCountry || ""}
            onChange={(value) => {
              const country = filteredDivisionCountries.find(c => c.alpha3code === value || c.id === value);
              if (country) {
                handleDivisionCountrySelect(country);
              }
            }}
            options={filteredDivisionCountries}
            placeholder="Division Country"
            searchValue={divisionCountrySearch}
            onSearchChange={setDivisionCountrySearch}
            isOpen={showDivisionCountryDropdown}
            setIsOpen={setShowDivisionCountryDropdown}
          />
        ) : (
          <TransferNewsCountryDropdown
            value={filters.divisionCountry || ""}
            onChange={() => {}}
            options={filteredDivisionCountries}
            placeholder="Division Country"
            searchValue={divisionCountrySearch || getSelectedDivisionCountryName()}
            onSearchChange={(value) => {
              setDivisionCountrySearch(value);
              if (!value) {
                handleDivisionCountrySelect({ name: "", alpha3code: "" });
              }
            }}
            isOpen={showDivisionCountryDropdown}
            setIsOpen={setShowDivisionCountryDropdown}
            onSelect={handleDivisionCountrySelect}
          />
        )}
      </div>

      {/* Competitions Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.competition_id || ""}
            onChange={(value) => {
              const competition = filteredCompetitions.find(c => c.id === value);
              if (competition) {
                handleCompetitionSelect(competition);
              }
            }}
            options={filteredCompetitions.map(c => ({
              id: c.id,
              name: c.name || c.title
            }))}
            placeholder="Division"
            displayProperty="name"
            valueProperty="id"
            noOptionsText={filters.divisionCountry ? "No competitions found" : "Select division country first"}
          />
        ) : (
          <TransferNewsCompetitionDropdown
            value={filters.competition_id || ""}
            onChange={() => {}}
            options={filteredCompetitions}
            placeholder="Division"
            searchValue={competitionSearch || getSelectedCompetitionName()}
            onSearchChange={(value) => {
              setCompetitionSearch(value);
              if (!value) {
                handleCompetitionSelect({ id: "", name: "" });
              }
            }}
            isOpen={showCompetitionDropdown}
            setIsOpen={setShowCompetitionDropdown}
            onSelect={handleCompetitionSelect}
            noOptionsText={filters.divisionCountry ? "No competitions found" : "Select division country first"}
          />
        )}
      </div>

      {/* Positions Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.position}
            onChange={handlePositionChange}
            options={positions}
            placeholder="All Positions"
            displayProperty="name"
            valueProperty="id"
          />
        ) : (
          <TransferNewsDropdown
            options={positions}
            value={filters.position}
            onChange={handlePositionChange}
            placeholder="All Positions"
            displayProperty="name"
            valueProperty="id"
          />
        )}
      </div>

      {/* Type Filter */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.type}
            onChange={handleTypeChange}
            options={[
              { value: "", label: "Transfer Type" },
              { value: "Transfer", label: "Transfer" },
              { value: "Loan", label: "Loan" },
              { value: "Free Agent", label: "Free Agent" },
            ]}
            placeholder="Transfer Type"
            displayProperty="label"
            valueProperty="value"
          />
        ) : (
          <TransferNewsDropdown
            options={[
              { value: "", label: "Transfer Type" },
              { value: "Transfer", label: "Transfer" },
              { value: "Loan", label: "Loan" },
              { value: "Free Agent", label: "Free Agent" },
            ]}
            value={filters.type}
            onChange={handleTypeChange}
            placeholder="Transfer Type"
            displayProperty="label"
            valueProperty="value"
          />
        )}
      </div>

      {/* Date From Filter */}
      <div className="relative w-full sm:w-auto sm:min-w-[160px]">
        <CustomDateTimePicker
          value={filters.date_from ? new Date(filters.date_from) : null}
          onChange={(date) => {
            const formattedDate = date ? date.toISOString().split("T")[0] : "";
            handleDateFromChange(formattedDate);
          }}
          placeholder="Date From"
          showTimeSelect={false}
          openToLeft={true}
          position="left-0"
          className={`bg-white border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'pl-4 pr-10 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 w-full`}
        />
      </div>

      {/* Date To Filter */}
      <div className="relative w-full sm:w-auto sm:min-w-[160px]">
        <CustomDateTimePicker
          value={filters.date_to ? new Date(filters.date_to) : null}
          onChange={(date) => {
            const formattedDate = date ? date.toISOString().split("T")[0] : "";
            handleDateToChange(formattedDate);
          }}
          placeholder="Date To"
          showTimeSelect={false}
          openToLeft={true}
          position="right-0"
          className={`bg-white border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'pl-4 pr-10 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 w-full`}
        />
      </div>
    </div>
  );
};

export default TransferNewsFilters;
